// Sub-broker trade — a merchant sells to his own clients with the desk's
// coins, and the desk's side of it is booked in the same breath (operator,
// 12 Sept 2026).
//
// THE CASE. "Loki is acting as a sub-broker, so whenever he uses treasury
// assets for buying/selling to his clients, there should be a counter leg
// posted with the desk as well. E.g. he sells 10k USDT to his client at 3.682,
// so he buys 10k from the desk at 3.672 and sells that same asset to his
// client, so the profit stays under his account. True for most of his trades
// but not all. Under his account I need a screen where I can do both legs in
// one entry whenever applicable."
//
// NOTHING NEW IN THE BOOKS. This screen posts up to three ORDINARY entries, in
// this order, each through the route the other screens already use:
//   (1) THE TRADE with the sub-broker at the DESK rate, always settle-later
//       (POST /api/trades, `unsettled: true`). Direction from the client's
//       side: his client BUYS coins ⇒ he buys them from the desk
//       (customer_buy — pays fiat, receives coins); his client SELLS ⇒
//       customer_sell. Both legs park on his box.
//   (2) THE CLIENT'S MONEY, in (POST /api/transfers, party → rail): for a
//       client-buy, fiat at the CLIENT rate into the rail it landed on; for a
//       client-sell, the client's coins into the desk wallet they reached.
//   (3) THE DELIVERY, out (POST /api/transfers, rail → party): for a
//       client-buy, the coins out of the wallet they left; for a client-sell,
//       fiat at the CLIENT rate out of the rail it was paid from.
// The sweep closes the trade from (2) and (3) exactly as it closes any
// settle-later trade, and what the two rates leave behind — quantity ×
// (client rate − desk rate) — stays on his box. That is HIS margin, settled
// between him and the desk separately; it is never the desk's income.
//
// EACH ENTRY KEEPS ITS OWN KEY. The deal id and the two references are held
// in refs (NewTrade's and RecordMoney's rule), so a retry after a lost answer
// dedupes rather than posting twice — and when (2) or (3) fails after (1) has
// landed, the screen says exactly what stands and the button retries only the
// entries that have not landed, never the ones that have.
//
// Reached from the party page ("Sub-broker trade", merchants only); lives at
// /parties/:slug/sub-broker so it carries the party and can be linked to.
// ?dir=buy|sell&qty=&desk=&client= prefill the form (client = the client rate).
//
// THE ARITHMETIC IS THE SHARED CALCULATOR (hooks/useTradeMath). Operator,
// 12 Sept 2026: "amount x rate = total, any 2 = autofill 3rd", "dynamic which
// changes with each number I change", "even if I directly paste the rates,
// total should be there". Two calculators, one per side, sharing the
// quantity: desk side = quantity, desk rate, desk total; client side = the
// same quantity, client rate, client total. Correcting a total re-derives
// that side's rate and the quantity stays ("I should be able to settle the
// trade by clicking on total"), and the two TOTALS — not quantity × rate —
// are what post, so the figure the operator settled on is the figure booked.
//
// 14 Sept 2026, the minimalist pass (SWEEP-SCOPE §5.2, §6, §7, item 42):
// the helper sentence under the toggle, the calculator rule, the KYT sentence
// and the running-account nuance live in "About this screen"; hints are one
// clause; Notes sit under More; Quantity sits beside Coin and Currency; the
// margin panel is two lines; the toggle names the coin; the pickers say
// "Pay-in account / Pay-out account"; the success line names the ids and the
// box in the §6 words. Mechanisms untouched.

import { useState, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type Direction, type Monetary } from '../lib/api';
import { tg } from '../lib/telegram';
import {
  ASSETS, isFiat, kindsForCategory, treasuryPath, partyPath, makeReference, DEFAULT_RAILS,
} from '../lib/workflow';
// ONE rateProblem, from lib/amount.ts (sweep item 21, 14 Sept 2026) — this
// screen carried its own copy, as did two others, so a rate refused at one
// door was accepted at another.
import { amountProblem, rateProblem } from '../lib/amount';
import { errorWords, isSentinel, nothingLanded, readWords } from '../lib/errors';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field, FieldRow } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { AccountsDown, ReadFailed, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { boxWords } from '../components/BoxLines';
import { MoreDetails } from '../components/MoreDetails';
import { Pending } from '../components/Pending';
import { AssetPicker } from '../components/AssetPicker';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import { AsOfChips } from '../components/AsOfChips';
import { useTradeMath } from '../hooks/useTradeMath';
import { partyName, foldAsset, sameAsset, partyBalanceOf } from '../lib/naming';
import { readState } from '../lib/read-state';
import {
  displayAsset, fmtAmount, fmtDate, fmtDay, dubaiDate,
  dubaiWallToIso, backdateProblem, shownAmount,
} from '../lib/format';

type Desk = 'dubaiotc' | 'indiaotc';
const isDesk = (v: unknown): v is Desk => v === 'dubaiotc' || v === 'indiaotc';

/** Which way the CLIENT is trading. The sub-broker mirrors it against the desk. */
export type ClientSide = 'buy' | 'sell';
/** The three entries, in posting order. */
type Step = 'trade' | 'money' | 'delivery';

/** The registry's coins and currencies — the same list the trade screen offers. */
const COINS: readonly string[] = ASSETS.filter((a) => !isFiat(a));
const FIATS: readonly string[] = ASSETS.filter((a) => isFiat(a));

/** The chain matters for the default wallet: the TRC hot wallet holds TRC
 *  coins only, so the exact code decides, not the fold. */
const isTrc = (coin: string) => coin === DEFAULT_RAILS.hotTrc.asset;   // dialect-ok: chain identity — the TRC hot wallet is the default for USDT·TRC alone, never for USDT·ERC

/** Fiat at two decimals, the way NewTrade derives a fiat total (`toFixed(2)`),
 *  so the figure on screen is the figure that posts. The cheque screen keeps a
 *  half-even helper because its ROUTE rounds half-even; the trade and transfer
 *  routes post what they are sent. */
const fiat2 = (n: number): number => Number(n.toFixed(2));

export interface SubBrokerInput {
  side: ClientSide;
  coin: string;
  fiat: string;
  qty: number;
  /** The two fiat TOTALS, from the calculators — not quantity × rate. A
   *  total the operator corrected to what was actually paid is what posts;
   *  the rate on that side has already re-derived from it (12 Sept 2026). */
  deskFiat: number;
  clientFiat: number;
}
export interface SubBrokerPlan {
  direction: Direction;
  /** The trade's legs, in the route's terms: take = what the sub-broker PAYS
   *  the desk, give = what he RECEIVES. */
  take: Monetary;
  give: Monetary;
  /** The desk total — the trade's fiat leg. */
  deskFiat: number;
  /** The client total — what the client's money is, or what goes out to him. */
  clientFiat: number;
  /** What stays on his box: clientFiat − deskFiat for a client-buy, deskFiat −
   *  clientFiat for a client-sell. Negative = he dealt worse than the desk rate. */
  margin: number;
  /** Entry (2): the client's money, IN. */
  moneyIn: Monetary;
  /** Entry (3): the delivery, OUT. */
  moneyOut: Monetary;
}

/**
 * THE TWO SHAPES, side by side.
 *
 *   client BUYS  → he buys from the desk  → customer_buy:  take fiat@desk, give coin
 *                  money in  = fiat@client (the client paid him, he passes it in)
 *                  money out = the coin     (the desk's coins go to his client)
 *                  margin    = fiat@client − fiat@desk
 *   client SELLS → he sells to the desk   → customer_sell: take coin, give fiat@desk
 *                  money in  = the coin     (his client's coins reach the desk)
 *                  money out = fiat@client (the desk pays what he owes his client)
 *                  margin    = fiat@desk − fiat@client
 *
 * The two totals arrive from the calculators (a derived total is already at
 * two decimals; a typed one may not be) and are held to the currency's two
 * decimals here — the ones that post — so the margin on screen is exactly
 * what will stand on his box.
 */
export function deriveSubBroker(i: SubBrokerInput): SubBrokerPlan {
  const deskFiat   = fiat2(i.deskFiat);
  const clientFiat = fiat2(i.clientFiat);
  const coinLeg:   Monetary = { asset: i.coin, amount: i.qty };
  const deskLeg:   Monetary = { asset: i.fiat, amount: deskFiat };
  const clientLeg: Monetary = { asset: i.fiat, amount: clientFiat };
  if (i.side === 'buy') {
    return { direction: 'customer_buy', take: deskLeg, give: coinLeg, deskFiat, clientFiat,
             margin: fiat2(clientFiat - deskFiat), moneyIn: clientLeg, moneyOut: coinLeg };
  }
  return { direction: 'customer_sell', take: coinLeg, give: deskLeg, deskFiat, clientFiat,
           margin: fiat2(deskFiat - clientFiat), moneyIn: coinLeg, moneyOut: clientLeg };
}

/** A figure and its unit as ONE value (a no-break space joins them): a line wraps between values, never inside one. */
const money = (a: number, asset: string, places?: number) => `${fmtAmount(a, asset, places)}\u00a0${displayAsset(asset)}`;

/** A box with its asset, in BoxLines' words (§6, 14 Sept 2026): "owes you
 *  36,720 AED", "you hold 10,000 USDT", "AED square". The local copy of the
 *  words went with the sweep; the dust rule and the number format are the
 *  shared ones. */
function boxWithAsset(v: number, asset: string): string {
  const a = displayAsset(foldAsset(asset));
  const w = boxWords(v, asset);
  return w === 'square' ? `${a} square` : `${w}\u00a0${a}`;
}

/** What has landed, by step — the record a retry reads before it posts.
 *  `prior`: the operator answered "It's already booked" to a twin, so this
 *  movement stood in the books BEFORE this entry set — it is already in the
 *  balances the projection starts from and must not be counted again
 *  (review, 12 Sept 2026). A `deduped` answer from the route is different:
 *  that is this screen's own earlier attempt landing. */
interface Landed {
  trade?:    { dealId: string; deduped: boolean };
  money?:    { reference: string; deduped: boolean; prior?: boolean };
  delivery?: { reference: string; deduped: boolean; prior?: boolean };
}

export function SubBrokerTrade() {
  const { slug } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const entity: Desk = isDesk(search.get('entity')) ? (search.get('entity') as Desk) : 'dubaiotc';
  const nav = useNavigate();

  // Same key as the party page, so arriving from it is a cache hit — and
  // refreshMoneyViews refetches it after every posting, which is how the
  // success line reads the LIVE box (see `posted.at`).
  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug!, entity),
    enabled:  !!slug,
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  // What each read is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED, 'waiting' while nothing has
  // answered (a first read, the retry of a failure, an offline pause), 'failed' on an error — whatever the cache holds.
  const partyRead = readState(q);
  const accounts = readState(treasury);

  const [side, setSide]             = useState<ClientSide>(search.get('dir') === 'sell' ? 'sell' : 'buy');
  const [coin, setCoin]             = useState<string>(DEFAULT_RAILS.hotTrc.asset);
  const [fiat, setFiat]             = useState<string>(DEFAULT_RAILS.cash.asset);
  // TWO CALCULATORS, ONE PER SIDE, SHARING THE QUANTITY. Each holds quantity
  // (coin), rate (fiat per coin, the pair's way round) and total (fiat); any
  // two fill the third on every keystroke. The desk's is the trade with the
  // desk; the client's is what his client paid or gets.
  const desk   = useTradeMath(coin, fiat);
  const client = useTradeMath(coin, fiat);
  // ONE QUANTITY BOX WRITES TO BOTH. When a side DERIVES the quantity instead
  // (desk total and desk rate pasted, no quantity typed) the other side must
  // hear it, or its total would never fill. The deriving side is the source
  // and the quantity lands on the other as if typed; when both sides hold it
  // as a fact they already agree. Runs during render — the hook's own way —
  // so the next paint shows both sides in step.
  const setQty = (v: string) => { desk.setAmount(coin, v); client.setAmount(coin, v); };
  const qty = desk.amountOf(coin);
  if (qty !== client.amountOf(coin)) {
    if (desk.derived === 'base')        client.preset({ asset: coin }, qty);
    else if (client.derived === 'base') desk.preset({ asset: coin }, client.amountOf(coin));
  }
  // ?qty= ?desk= ?client= count as typed first, quantity before rate, so a
  // rate typed afterwards moves the total and the quantity anchors. Once, on
  // the first render, before the first paint.
  const prefilled = useRef(false);
  if (!prefilled.current) {
    prefilled.current = true;
    const qtyParam = search.get('qty') ?? '', deskParam = search.get('desk') ?? '', clientParam = search.get('client') ?? '';
    if (qtyParam)    { desk.preset({ asset: coin }, qtyParam); client.preset({ asset: coin }, qtyParam); }
    if (deskParam)   desk.preset('rate', deskParam);
    if (clientParam) client.preset('rate', clientParam);
  }
  const [clientName, setClientName] = useState('');
  const [notes, setNotes]           = useState('');
  // Which figure box has the cursor — a box the calculator filled shows its figure grouped, four places at most
  // (shownAmount, display only); the one being edited shows the raw string, so a formatter never fights the typing.
  const [editing, setEditing] = useState<'qty' | 'deskTotal' | 'clientTotal' | null>(null);
  const [when, setWhen]             = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // Both movements default ON — the operator's own example has the client's
  // money and the coins both moved on the day. Untick the one that has not.
  //
  // EXCEPT THE CURRENCY LEG WHEN THE CURRENCY IS INR (sweep item 36, 14 Sept
  // 2026): angadiya money lands days later, so on the day of the trade the
  // client's INR (a client-buy) or the INR out to him (a client-sell) has
  // usually not moved yet — it starts unticked and is recorded from his page
  // when it lands. Re-applied when the currency chip or the side changes;
  // after that the ticks are the operator's.
  const [moneyOn, setMoneyOn]       = useState(true);
  const [deliveryOn, setDeliveryOn] = useState(true);
  const applyMovementDefaults = (s: ClientSide, f: string) => {
    const lags = sameAsset(f, 'INR');
    setMoneyOn(!(lags && s === 'buy'));
    setDeliveryOn(!(lags && s === 'sell'));
  };
  const pickSide = (s: ClientSide) => { setSide(s); applyMovementDefaults(s, fiat); };
  // The fiat rail serves the fiat movement (in on a client-buy, out on a
  // client-sell); the wallet serves the coin movement (the other way round).
  const [fiatRail, setFiatRail] = useState<RailValue>({ ...DEFAULT_RAILS.cash });
  const [wallet, setWallet]     = useState<RailValue>({ ...DEFAULT_RAILS.hotTrc });

  // A chip change re-defaults its rail: the office cash rail holds AED, the
  // TRC hot wallet holds USDT·TRC, and neither is a sane default for anything
  // else — a blank picker asks the question instead of answering it wrongly.
  const pickFiat = (f: string) => {
    setFiat(f);
    setFiatRail(sameAsset(f, DEFAULT_RAILS.cash.asset) ? { ...DEFAULT_RAILS.cash } : { slug: '', asset: '' });
    applyMovementDefaults(side, f);
  };
  const pickCoin = (c: string) => {
    setCoin(c);
    setWallet(isTrc(c) ? { ...DEFAULT_RAILS.hotTrc } : { slug: '', asset: '' });
  };
  // THE WALLET DECIDES THE CHAIN (RecordMoney's rule). A wallet row speaks the
  // chain code (USDTERC) or the fold (USDT); when it names a chain that is the
  // same coin as the chip but a different chain, the chip follows — the coins
  // that leave the ERC wallet are ERC coins, and the trade's leg must say so.
  // A different coin altogether is a mismatch, flagged below, never adopted.
  const pickWallet = (v: RailValue) => {
    setWallet(v);
    const chain = COINS.find((a) => a === v.asset && sameAsset(a, coin));   // dialect-ok: exact chain code, guarded by the fold on the same line
    if (chain) setCoin(chain);
  };
  const walletMismatch   = !!wallet.asset   && !sameAsset(wallet.asset, coin);
  const fiatRailMismatch = !!fiatRail.asset && !sameAsset(fiatRail.asset, fiat);

  // Everything the plan needs is read from the calculators: the quantity, the
  // two rates (posted as the trade's rate and in its notes) and the two
  // totals (posted as the legs and the movements).
  const deskTotal   = desk.amountOf(fiat);
  const clientTotal = client.amountOf(fiat);
  // WHICH BOX MOVES, so the operator sees it at a glance: "auto" on the label
  // and muted figures (operator, 12 Sept 2026). Each side's calculator says
  // which of its three it fills. The quantity box is shared by both, so it
  // reads "auto" only when BOTH sides derive it — a quantity one side derives
  // lands on the other as a fact (the sync above), and a fact on either side
  // is a fact in the shared box.
  const qtyAuto = desk.isDerived(coin) && client.isDerived(coin);
  const qtyN         = Number(qty) || 0;
  const deskN        = Number(desk.rate) || 0;
  const clientN      = Number(client.rate) || 0;
  const deskFiatN    = Number(deskTotal) || 0;
  const clientFiatN  = Number(clientTotal) || 0;
  const qtyWords         = amountProblem(qty);
  const deskWords        = rateProblem(desk.rate);
  const clientWords      = rateProblem(client.rate);
  const deskTotalWords   = amountProblem(deskTotal);
  const clientTotalWords = amountProblem(clientTotal);
  const plan = useMemo(() => {
    if (qtyN <= 0 || deskN <= 0 || clientN <= 0 || deskFiatN <= 0 || clientFiatN <= 0
      || qtyWords || deskWords || clientWords || deskTotalWords || clientTotalWords) return null;
    return deriveSubBroker({ side, coin, fiat, qty: qtyN, deskFiat: deskFiatN, clientFiat: clientFiatN });
  }, [side, coin, fiat, qtyN, deskN, clientN, deskFiatN, clientFiatN, qtyWords, deskWords, clientWords, deskTotalWords, clientTotalWords]);

  // Which rail each movement goes through, this side round.
  const moneyRail    = side === 'buy' ? fiatRail : wallet;
  const deliveryRail = side === 'buy' ? wallet   : fiatRail;
  const moneyMismatch    = side === 'buy' ? fiatRailMismatch : walletMismatch;
  const deliveryMismatch = side === 'buy' ? walletMismatch   : fiatRailMismatch;
  const moneyNoun    = side === 'buy' ? "the client's money" : "the client's coins";
  const deliveryNoun = side === 'buy' ? 'the coins' : 'the money';

  // ── submit ───────────────────────────────────────────────────────────────
  // THREE KEYS, EACH HELD ACROSS A RETRY. The deal id and the two references
  // are minted once and kept in refs (NewTrade, RecordMoney); `landedRef` is
  // the record of what the books have answered, read synchronously by the
  // next attempt so it skips what already stands. `landed` mirrors it for
  // the screen.
  const dealIdRef  = useRef<string | null>(null);
  const rcptRef    = useRef<string | null>(null);
  const payRef     = useRef<string | null>(null);
  const landedRef  = useRef<Landed>({});
  const [landed, setLanded] = useState<Landed>({});
  // The repeat guard is shared with every posting screen. Each of the three
  // entries can meet it, so the screen remembers WHICH one was wrapped when a
  // twin came back (`asking`), and "post it again anyway" confirms that one.
  const { twin, clearTwin, guard } = useRepeatGuard();
  const askingRef    = useRef<Step>('trade');
  // WHICH guard refused, by step (27 Oct 2026): the BFF takes `confirm_repeat` for a twin and `confirm_money_moved_again` for a settled
  // trade that already moved this money — one yes answers one question (routes/transfers.ts). The two movements are transfers with the
  // merchant at one end, so each can meet either; a yes is kept as `${step}:${kind}` and goes out as the flag that answers the refusal it
  // was given to. A yes that always went out as confirm_repeat left a money twin on a movement refused for ever behind its own panel.
  const confirmedRef = useRef<Set<string>>(new Set());

  const [posted, setPosted] = useState<{
    at: number; asOf: string | null; name: string; side: ClientSide;
    dealId: string; tradeDeduped: boolean;
    money: Landed['money'] | null; delivery: Landed['delivery'] | null;
    fiat: string; coin: string; margin: number;
    /** Projected boxes, from the balances the plan was derived on; the
     *  success line prefers the LIVE figures once the party is re-read. */
    fiatAfter: number; coinAfter: number;
  } | null>(null);

  const post = async () => {
    if (!plan || !slug || !q.data) throw new Error('nothing to book');
    const name  = partyName(q.data.party);
    const party = partyPath(slug, q.data.party.party_type, entity);
    const who   = clientName.trim() || '—';
    const L = landedRef.current;

    // (1) THE TRADE, at the desk rate, settle-later.
    if (!L.trade) {
      // The id carries the day the trade HAPPENED, like NewTrade's.
      dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
      const dealId = dealIdRef.current;
      askingRef.current = 'trade';
      const data = await guard(() => api.bookTrade({
        entity,
        direction:  plan.direction,
        party_slug: slug,
        // Nothing moves in this entry — the two movements below are what
        // moved, and the sweep matches them to these legs.
        unsettled:   true,
        amount_take: plan.take,
        amount_give: plan.give,
        // The desk rate as it stands in its box — typed, or re-derived from
        // a corrected desk total.
        rate:        desk.rate.trim(),
        deal_id:     dealId,
        notes: [
          `sub-broker · client ${who} @ ${client.rate.trim()} ${displayAsset(fiat)} per ${displayAsset(coin)}`,
          notes.trim(),
        ].filter(Boolean).join(' · '),
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('trade:repeat') || undefined,
        confirm_money_moved_again: confirmedRef.current.has('trade:money') || undefined,
      }));
      L.trade = { dealId: data.deal_id ?? dealId, deduped: !!data.deduped };
      setLanded({ ...L });
    }
    const dealId = L.trade.dealId;

    // (2) THE CLIENT'S MONEY, in: party → rail.
    if (moneyOn && !L.money) {
      rcptRef.current ??= makeReference('RCPT', whenIso || undefined);
      const reference = rcptRef.current;
      askingRef.current = 'money';
      const data = await guard(() => api.transfer({
        source:      party,
        destination: treasuryPath(moneyRail.slug, plan.moneyIn.asset, entity),
        amount:      plan.moneyIn,
        fee_payer:   'house',
        reference,
        notes:       `from ${who} for ${name} · sub-broker trade ${dealId}`,
        // Links this leg back to (1)'s deal, so Home's journal can fold it
        // with the delivery leg below into one row instead of two — the
        // notes sentence above already said "sub-broker trade {dealId}" in
        // words; this is the same fact as a field a screen can read without
        // parsing a sentence. `linked_deal_id`, never `deal_id`: this leg is
        // an ordinary transfer, not the deal's own leg, and `deal_id` here
        // would make the Reverse door refuse it while trade-void never
        // reverses a transfer either (Mechanisms, transfers.ts, 30 Sept –
        // 1 Oct 2026).
        linked_deal_id: dealId,
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('money:repeat') || undefined,
        confirm_money_moved_again: confirmedRef.current.has('money:money') || undefined,
      }));
      L.money = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // (3) THE DELIVERY, out: rail → party.
    if (deliveryOn && !L.delivery) {
      payRef.current ??= makeReference('PAY', whenIso || undefined);
      const reference = payRef.current;
      askingRef.current = 'delivery';
      const data = await guard(() => api.transfer({
        source:      treasuryPath(deliveryRail.slug, plan.moneyOut.asset, entity),
        destination: party,
        amount:      plan.moneyOut,
        fee_payer:   'house',
        reference,
        notes:       `to ${who} for ${name} · sub-broker trade ${dealId}`,
        linked_deal_id: dealId,
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('delivery:repeat') || undefined,
        confirm_money_moved_again: confirmedRef.current.has('delivery:money') || undefined,
      }));
      L.delivery = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // WHAT THE BOX WILL READ, projected from the balances in hand. Sign law:
    // what he pays (take, or money out to him) lifts his box, what he
    // receives (give, or money in from him) lowers it.
    // A movement answered "It's already booked" was posted before this entry
    // set, so the balances in hand already carry it — projecting it again
    // would count it twice (review, 12 Sept 2026).
    const bal = q.data.balances;
    const fiatNow = bal[foldAsset(fiat)] ?? bal[fiat] ?? 0;
    const coinNow = bal[foldAsset(coin)] ?? bal[coin] ?? 0;
    const moneyNew    = !!L.money    && !L.money.prior;
    const deliveryNew = !!L.delivery && !L.delivery.prior;
    const fiatAfter = side === 'buy'
      ? fiatNow + plan.deskFiat - (moneyNew ? plan.clientFiat : 0)
      : fiatNow - plan.deskFiat + (deliveryNew ? plan.clientFiat : 0);
    const coinAfter = side === 'buy'
      ? coinNow - qtyN + (deliveryNew ? qtyN : 0)
      : coinNow + qtyN - (moneyNew ? qtyN : 0);
    return {
      at: Date.now(), asOf: whenIso || null, name, side,
      dealId, tradeDeduped: L.trade.deduped,
      money: L.money ?? null, delivery: L.delivery ?? null,
      fiat, coin, margin: plan.margin, fiatAfter, coinAfter,
    };
  };

  const submit = useMutation({
    mutationFn: post,
    onSuccess: (r) => {
      setPosted(r);
      // Every key leaves with the entry set it was minted for.
      landedRef.current = {}; setLanded({});
      dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
      confirmedRef.current.clear(); clearTwin();
      // A clean slate for the next one. The desk rate, the coin, the currency
      // and the rails stay — the next of his trades that day shares them. The
      // rate is re-seated as the desk side's one fact, so it stays whether it
      // was typed or re-derived from a corrected total; the client side
      // starts empty.
      const deskRate = desk.rate;
      desk.reset(); client.reset();
      if (deskRate) desk.preset('rate', deskRate);
      setClientName(''); setNotes(''); setWhen('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  // Once the trade has landed the figures are locked to it: a changed
  // quantity or rate would make the movements disagree with the legs they are
  // meant to close. The rails and the two ticks stay live, so a wrong rail
  // can be corrected and the remaining entries retried.
  //
  // THE SAME LOCK WHEN THE TRADE MAY HAVE LANDED. A 5xx, a dropped connection
  // or a lost answer on the trade POST keeps the deal id (lib/errors.ts
  // nothingLanded), so the retry dedupes — to the figures FIRST sent. Left
  // editable, a changed quantity or rate would have the trade stand at the
  // old figures while the money and the delivery post at the new ones
  // (review, 12 Sept 2026). So the figures lock the moment a failure is not
  // provably answered before the first ledger write, and only "start another"
  // — a decision, not a keystroke — lets them go.
  const mayHold = submit.isError && !nothingLanded(submit.error);
  const locked  = !!landed.trade || mayHold;
  const busy    = submit.isPending;

  // THE KEYS CARRY THE DAY, so a change of date re-mints them — unless the
  // books may already hold one: a posting in flight, or a last failure not
  // provably answered before the first ledger write (NewTrade's rule,
  // lib/errors.ts nothingLanded). The field is disabled once the trade has
  // landed or may have, so the two references can never drift from the
  // deal's day; the check here is the belt to that brace.
  const onWhenChange = (v: string) => {
    setWhen(v);
    if (!submit.isPending && !mayHold && !landedRef.current.trade) {
      dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
      confirmedRef.current.clear(); clearTwin();
    }
  };

  // Everything from this attempt is dropped; what the books hold stands, and
  // the message above the button has already said what to record by hand.
  const startAnother = () => {
    landedRef.current = {}; setLanded({});
    dealIdRef.current = null; rcptRef.current = null; payRef.current = null;
    confirmedRef.current.clear(); clearTwin(); submit.reset();
  };

  // A READ THAT FAILED IS NOT AN EMPTY ONE — said in one line, with the way out and a way back.
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;
  // THE SCREEN IS REPLACED ONLY WHEN THERE IS NOTHING TO SHOW (Astra, 22 Oct 2026; ConvertBalance's rule). Every posting refreshes the
  // party; a refresh that fails over the party in hand used to unmount the form — and with it "✓ Booked", the references, and the
  // checklist of what stands after a part-way failure — a booking that LANDED, hidden behind "Couldn't read this party". With the
  // party cached the form and the answer stay, and the banner below says the failure once, beside them.
  if (partyRead === 'failed' && !q.data) {
    return (
      <div className="space-y-3 lg:max-w-[720px] lg:mx-auto">
        <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
        <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} tone="bad" />
      </div>
    );
  }
  if (!q.data)   return <Pending />;

  const name = partyName(q.data.party);
  const partyHref = `/parties/${encodeURIComponent(q.data.party.slug)}${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  // THE RUNNING ACCOUNT decides whether his trade closes by itself: only a
  // party marked with it has a short leg count as settled, the remainder on
  // his balance (lib/api.ts PartySummary). Read from the party, never assumed
  // — About this screen says which case he is (review, 12 Sept 2026).
  const rides = !!q.data.party.trade_remainder_rides_on_account;
  // MERCHANTS ONLY — the party page shows the door to a counterparty alone,
  // but a pasted URL reaches here for anyone. A customer or a rail has no
  // settle-later account to park the legs on.
  if (q.data.party.party_type !== 'counterparty') {
    return (
      <div className="space-y-3 lg:max-w-[720px] lg:mx-auto">
        <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
        <StateLine tone="warn" more="Sub-broker trades are for merchants only." action={<Link to={partyHref} className="btn-link text-meta">Party page →</Link>}>
          {name} is not a merchant.
        </StateLine>
      </div>
    );
  }

  // A REFRESH THAT FAILED keeps the last good read beside the error (TanStack v5): the form may stay on screen, but nothing is CLAIMED
  // from that read and nothing is posted off it — design system Appendix B. This is the one place the party's read becomes "unknown"
  // (`readState`, not 'known'): the box line of the answer and every post door below follow it, and Try again lifts it. (A first read
  // that has not answered never gets here: the guards above return.)
  const unknown = partyRead !== 'known';
  const rows = treasury.data?.rows ?? [];
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party.
  const accountsDown = accounts === 'failed' && !treasury.data;
  const live = posted && q.dataUpdatedAt > posted.at;
  const fiatAfter = posted ? (live ? partyBalanceOf(q.data.balances, posted.fiat) : posted.fiatAfter) : 0;
  const coinAfter = posted ? (live ? partyBalanceOf(q.data.balances, posted.coin) : posted.coinAfter) : 0;

  // THE BUTTON NAMES THE ENTRIES (sweep item 36): "Book · trade + money in +
  // coins out" on a client-buy with both movements ticked; each unticked
  // movement drops out of the words, so the label is the list of what posts.
  const entryWords = [
    'trade',
    moneyOn    ? (side === 'buy' ? 'money in'  : 'coins in')  : null,
    deliveryOn ? (side === 'buy' ? 'coins out' : 'money out') : null,
  ].filter(Boolean).join(' + ');
  const remaining = (landed.trade ? 0 : 1) + (moneyOn && !landed.money ? 1 : 0) + (deliveryOn && !landed.delivery ? 1 : 0);
  const ready = !busy && !!plan && !whenWords
    && (!moneyOn    || (!!moneyRail.slug    && !moneyMismatch))
    && (!deliveryOn || (!!deliveryRail.slug && !deliveryMismatch));
  // The figures and the accounts are in order but the party could not be re-read: nothing is posted off a box nobody has read
  // since the last posting (the retry of a part-way set included) — Try again first.
  const canPost = ready && !unknown;
  // EVERY DOOR THAT POSTS asks the same question in its own CALLBACK, not only in its button's state — a disabled button is a label,
  // not a lock, and the repeat panel's doors are callbacks a screen hands away. Nothing is posted off a party nobody could re-read.
  const send = (needsReady = false) => { if (unknown || (needsReady && !ready)) return; submit.mutate(); };
  const coinName = displayAsset(coin);
  // THE TOGGLE NAMES THE COIN BY ITS FOLD (§6 trade direction, review
  // 14 Sept 2026): "Buys USDT / Sells USDT" on both trade screens — New trade
  // folds the chain off the base too. The chip, the Quantity hint and the
  // readback keep the exact chain code (coinName); only the toggle folds.
  const coinWord = displayAsset(foldAsset(coin));

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      <ScreenHeader
        title="Sub-broker trade"
        subtitle="His trade, the client’s money in, the coins out"
        help={
          <>
            <p>
              Three ordinary entries in one tap. <strong className="font-medium text-tg-text">The trade</strong> with
              {' '}{name} at the desk rate, settle later: when the client buys coins, {name} buys them from the desk
              at the desk rate and sells them on at his rate; when the client sells, {name} buys them from the
              client at his rate and sells them to the desk. <strong className="font-medium text-tg-text">The
              client&rsquo;s money</strong> in — the currency at the client rate on the account it landed on, or
              the client&rsquo;s coins into the wallet they reached. <strong className="font-medium text-tg-text">The
              delivery</strong> out — the coins from the wallet they left, or the currency at the client rate
              from the account it was paid from.
            </p>
            <p>
              The sweep closes the trade from the two movements as it closes any settle-later trade. What the
              two rates leave behind — the client total less the desk total — is his margin: it stays on his
              account, settled with him separately, never the desk&rsquo;s income. Untick a movement that has
              not happened yet and record it from his page when it does
              {/* Whether the trade then closes by itself depends on the party,
                  not on the screen: only one marked as carrying a running
                  account has a short leg count as settled, the remainder on
                  his balance (review, 12 Sept 2026). */}
              {rides
                ? <> — the trade then closes itself: {name} carries a running account and the margin rides on his balance.</>
                : <>. {name} does not carry a running account, so the trade closes only once the movements cover it in full; a shortfall of his margin stays open on his account until it is settled with him.</>}
            </p>
            <p>
              <strong className="font-medium text-tg-text">The figures.</strong> On each side, any two of quantity,
              rate and total fill the third; the quantity is the same on both sides. Correct a total to what was
              actually paid and that side&rsquo;s rate follows — the quantity stays. The totals are what post.
            </p>
            <p>
              <strong className="font-medium text-tg-text">When did this happen?</strong> All three entries carry
              the same instant, so the sweep matches them to each other. Dubai time. The client&rsquo;s wallet is
              not a desk party: KYT does not screen it unless you add it on the KYT screen.
            </p>
          </>
        }
      />

      {/* THE CLIENT'S SIDE, not the sub-broker's — the operator hears "he sold
          10k to his client", and the desk's direction follows from that. The
          toggle names the coin, as Book trade's does (§6). */}
      <Field label="His client">
        <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
          {(['buy', 'sell'] as const).map((v) => (
            <button
              key={v}
              type="button"
              disabled={locked || busy}
              onClick={() => pickSide(v)}
              className={`seg py-2 lg:py-1.5 disabled:opacity-60 ${side === v ? 'seg-on' : ''}`}
            >
              {/* The coin bold, the verb plain (emphasis rule). */}
              {v === 'buy' ? 'Buys' : 'Sells'} <b className="font-semibold">{coinWord}</b>
            </button>
          ))}
        </div>
      </Field>

      {/* The everyday coin and currencies as chips, the rest behind "more…"
          (operator, 12 Sept 2026: "only most used 3 … rest under drop
          down"). A locked or busy form ignores a pick, as the chips did.
          Quantity sits beside them (§5.2). Every figure box passes what was
          typed straight to its calculator — no coercion, no trimming — so a
          rate pasted from Telegram lands as typed and the other boxes move
          with each keystroke. */}
      <FieldRow cols={3}>
        <Field label="Coin">
          <AssetPicker value={coin} onChange={(a) => { if (!locked && !busy) pickCoin(a); }} options={COINS} quick={['USDTTRC']} />
        </Field>
        <Field label="Currency">
          <AssetPicker value={fiat} onChange={(a) => { if (!locked && !busy) pickFiat(a); }} options={FIATS} quick={['AED', 'INR']} />
        </Field>
        <Field label="Quantity" hint={qtyAuto ? `${coinName} · auto` : coinName}>
          <input className={`input${qtyAuto ? ' text-tg-hint' : ''}`} value={shownAmount(qty, coin, qtyAuto, editing === 'qty')} onChange={(e) => setQty(e.target.value)} onFocus={() => setEditing('qty')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={locked || busy} aria-label="Quantity" />
          <ErrorLine>{qtyWords}</ErrorLine>
        </Field>
      </FieldRow>

      {/* THE TWO CALCULATOR PAIRS: rate | total per side from lg; on the phone
          the four boxes stack full width, as the Rate box does on Book trade,
          the cheque and Convert (19 Sept 2026, the phone pass, reviewed). A
          2×2 was tried and dropped: the derived-rate hint "AED per USDT·TRC ·
          auto" is ~122px at the hint's 10px and the Field caps a hint at 60%
          of its column, ~102–118px in a half column at 375–430px — the "auto"
          marker the operator asked for (12 Sept 2026) fell off every phone. */}
      <FieldRow>
        <Field label="Desk rate" hint={desk.rateIsDerived ? `${desk.rateWords} · auto` : desk.rateWords}>
          <input className={`input${desk.rateIsDerived ? ' text-tg-hint' : ''}`} value={desk.rate} onChange={(e) => desk.setRate(e.target.value)} inputMode="decimal" disabled={locked || busy} aria-label="Desk rate" />
          <ErrorLine>{deskWords}</ErrorLine>
        </Field>
        <Field label="Desk total" hint={desk.isDerived(fiat) ? `${displayAsset(fiat)} · auto` : displayAsset(fiat)}>
          <input className={`input${desk.isDerived(fiat) ? ' text-tg-hint' : ''}`} value={shownAmount(deskTotal, fiat, desk.isDerived(fiat), editing === 'deskTotal')} onChange={(e) => desk.setAmount(fiat, e.target.value)} onFocus={() => setEditing('deskTotal')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={locked || busy} aria-label="Desk total" />
          <ErrorLine>{deskTotalWords}</ErrorLine>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Client rate" hint={client.rateIsDerived ? `${client.rateWords} · auto` : client.rateWords}>
          <input className={`input${client.rateIsDerived ? ' text-tg-hint' : ''}`} value={client.rate} onChange={(e) => client.setRate(e.target.value)} inputMode="decimal" disabled={locked || busy} aria-label="Client rate" />
          <ErrorLine>{clientWords}</ErrorLine>
        </Field>
        <Field label="Client total" hint={client.isDerived(fiat) ? `${displayAsset(fiat)} · auto` : displayAsset(fiat)}>
          <input className={`input${client.isDerived(fiat) ? ' text-tg-hint' : ''}`} value={shownAmount(clientTotal, fiat, client.isDerived(fiat), editing === 'clientTotal')} onChange={(e) => client.setAmount(fiat, e.target.value)} onFocus={() => setEditing('clientTotal')} onBlur={() => setEditing(null)} inputMode="decimal" disabled={locked || busy} aria-label="Client total" />
          <ErrorLine>{clientTotalWords}</ErrorLine>
        </Field>
      </FieldRow>

      {/* THE MARGIN, LIVE, before anything is booked — the one figure the
          operator is doing this for, in two lines (§5.2). Amber when he dealt
          worse than the desk rate: possible, but worth a second look. The
          margin is the difference of the two TOTALS — the figures that post —
          not quantity × rate difference, which drifts from it the moment a
          total is corrected. Line 2 is the confirm sentence for the three
          entries. On the phone the panel reads at the card size (13px) and
          the three entries stand one per line — the " · " that joins them
          on the desktop line is hidden there — so the readback is three
          short lines, not one sentence wrapped over four (19 Sept 2026). */}
      {plan && (
        <div className="rounded-lg border border-stroke-soft bg-panel px-3 py-2 text-meta max-lg:text-body leading-relaxed space-y-0.5">
          {/* 21 Oct 2026 audit: the two totals are the boxes above, and the money in / out the cards below — the
              panel says only what neither does: the margin, and the trade it comes from. */}
          <p className={`text-body ${plan.margin < 0 ? 'text-warn' : 'text-tg-text'}`}>
            Margin <span className="amt-key whitespace-nowrap">{fmtAmount(plan.margin, fiat, 2)} {displayAsset(fiat)}</span>
            {' '}· stays on his account{plan.margin < 0 && ' · ⚠ below the desk rate'}
          </p>
          <p className="text-tg-hint">
            <span className="who">{name}</span> {side === 'buy' ? 'pays' : 'receives'} <span className="amt whitespace-nowrap">{fmtAmount(plan.deskFiat, fiat, 2)} {displayAsset(fiat)}</span> and
            {' '}{side === 'buy' ? 'receives' : 'pays'} <span className="amt whitespace-nowrap">{fmtAmount(qtyN, coin)} {coinName}</span> at <span className="amt">{desk.rate.trim()}</span>
          </p>
        </div>
      )}


      <FieldRow>
        <Field label="Client" hint="optional">
          <input className="input" value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={locked || busy} aria-label="Client" />
        </Field>
        {/* "AS OF" — the shared chip row (operator, 14 Sept 2026), in place of
            the date box and its two helper lines. The screen keeps `when`,
            whenIso and whenWords; the control only renders the choice. */}
        <AsOfChips label="When did this happen?" value={when} onChange={onWhenChange} disabled={locked || busy} />
      </FieldRow>

      {accounts === 'failed' && (
        <StateLine tone="warn" more={readWords(treasury.error)} action={retry(treasury.refetch)}>
          Couldn&rsquo;t read the accounts — try again.
        </StateLine>
      )}

      {/* THE TWO MOVEMENTS, each with the account it went through. Ticked by
          default; an unticked one is left for the party page, and the trade
          waits on it. Once posted, a tick reads as done. A plain grid, so the
          two boxes stretch to one height when one shows an error (§5.2). */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
        <MovementBox
          checked={moneyOn}
          onChange={setMoneyOn}
          done={landed.money?.reference ?? null}
          disabled={busy || !!landed.money}
          label={side === 'buy' ? "The client's money has arrived" : "The client's coins have arrived"}
          detail={plan
            ? `Records ${money(plan.moneyIn.amount, plan.moneyIn.asset, isFiat(plan.moneyIn.asset) ? 2 : undefined)} in from ${name}${side === 'buy' ? ' at the client rate' : ''}.`
            : side === 'buy' ? 'Records quantity × client rate in from him.' : 'Records the coins in from him.'}
        >
          {accountsDown ? <AccountsDown value={moneyRail} /> : (
          <RailPicker
            value={moneyRail}
            onChange={side === 'buy' ? setFiatRail : pickWallet}
            rows={rows}
            // The broker's own habits rank the list (12 Sept 2026): the
            // screen lives under his party page, so he is always in context.
            partySlug={slug}
            partyEntity={entity}
            allowedKinds={kindsForCategory(side === 'buy' ? 'fiat' : 'crypto')}
            loading={treasury.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder="Pay-in account"
          />
          )}
          <ErrorLine>
            {moneyOn && moneyMismatch
              && `That account holds ${displayAsset(moneyRail.asset)}, not ${displayAsset(plan?.moneyIn.asset ?? (side === 'buy' ? fiat : coin))} — check the account.`}
          </ErrorLine>
        </MovementBox>

        <MovementBox
          checked={deliveryOn}
          onChange={setDeliveryOn}
          done={landed.delivery?.reference ?? null}
          disabled={busy || !!landed.delivery}
          label={side === 'buy' ? 'The coins have gone out' : 'The money has gone out'}
          detail={plan
            ? `Records ${money(plan.moneyOut.amount, plan.moneyOut.asset, isFiat(plan.moneyOut.asset) ? 2 : undefined)} out to ${name}${side === 'sell' ? ' at the client rate' : ''}.`
            : side === 'buy' ? 'Records the coins out to him.' : 'Records quantity × client rate out to him.'}
        >
          {accountsDown ? <AccountsDown value={deliveryRail} /> : (
          <RailPicker
            value={deliveryRail}
            onChange={side === 'buy' ? pickWallet : setFiatRail}
            rows={rows}
            partySlug={slug}
            partyEntity={entity}
            allowedKinds={kindsForCategory(side === 'buy' ? 'crypto' : 'fiat')}
            loading={treasury.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder="Pay-out account"
          />
          )}
          <ErrorLine>
            {deliveryOn && deliveryMismatch
              && `That account holds ${displayAsset(deliveryRail.asset)}, not ${displayAsset(plan?.moneyOut.asset ?? (side === 'buy' ? coin : fiat))} — check the account.`}
          </ErrorLine>
        </MovementBox>
      </div>

      {/* The fold: Notes (typed on 6% of trades). */}
      <MoreDetails label="More">
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" disabled={locked || busy} />
        </Field>
      </MoreDetails>

      {/* A refresh that FAILED while the party is in hand (a posting refreshes it): the form and the outcome below stay, and this is the
          warning beside them — amber, said once, with Try again. */}
      {unknown && <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} />}

      {/* WHAT STANDS SO FAR — shown the moment the first entry lands and
          until all of them have, so a refusal on the second or third entry is
          never read as "nothing was booked". */}
      {landed.trade && (
        <div className="rounded-lg border border-stroke-soft bg-panel px-3 py-2 text-meta max-lg:text-body space-y-0.5">
          <p className="text-tg-text">
            <span className="text-pos">✓</span> Trade booked as <span className="font-mono">{landed.trade.dealId}</span>
            {landed.trade.deduped && <span className="text-tg-hint"> (already in the books — kept)</span>}
          </p>
          <p className={landed.money ? 'text-tg-text' : 'text-tg-hint'}>
            {landed.money
              ? <><span className="text-pos">✓</span> {moneyNoun} recorded as <span className="font-mono">{landed.money.reference}</span></>
              : moneyOn ? <>— {moneyNoun}: not recorded yet</> : <>— {moneyNoun}: left for the party page</>}
          </p>
          <p className={landed.delivery ? 'text-tg-text' : 'text-tg-hint'}>
            {landed.delivery
              ? <><span className="text-pos">✓</span> {deliveryNoun} recorded as <span className="font-mono">{landed.delivery.reference}</span></>
              : deliveryOn ? <>— {deliveryNoun}: not recorded yet</> : <>— {deliveryNoun}: left for the party page</>}
          </p>
          <p className="text-tg-hint pt-1">Figures are locked to the booked trade. <button type="button" className="btn-link text-meta" onClick={startAnother} disabled={busy}>Start another instead</button></p>
        </div>
      )}

      {/* A REFUSAL AFTER THE TRADE LANDED says exactly what stands and what
          does not, in the party page's words, so the operator can finish by
          hand if the retry will not do. Above the button (§7 rule 11). */}
      {submit.isError && !isSentinel(submit.error) && (
        landed.trade ? (
          // What stands is the checklist above (each entry, with its reference); this says what does not, once.
          <div className="rounded-lg bg-warn-bg p-3 text-body space-y-1">
            <StateLine
              tone="warn"
              more="Record it from the party page, or retry the remaining entries below."
              action={<Link to={partyHref} className="btn-link text-meta">Party page →</Link>}
            >
              Trade booked — {!landed.money && moneyOn ? (side === 'buy' ? 'money in' : 'coins in') : (side === 'buy' ? 'coins out' : 'money out')} NOT recorded.
            </StateLine>
            <p className="text-meta text-tg-hint">{errorWords(submit.error)}</p>
          </div>
        ) : nothingLanded(submit.error) ? (
          <ErrorLine className="mt-0 text-body">{errorWords(submit.error)} Nothing was booked.</ErrorLine>
        ) : (
          // The trade POST failed without a refusal from before the first
          // ledger write — a 5xx, a dropped connection, a lost answer — so the
          // trade MAY stand under its id. Never "nothing was booked" here
          // (lib/errors.ts); the id and the figures are held for the retry,
          // which dedupes (review, 12 Sept 2026).
          <div className="space-y-1">
            <ErrorLine className="mt-0 text-body">{errorWords(submit.error)}</ErrorLine>
            <StateLine
              tone="warn"
              more={<>
                The figures are locked to the booked trade: the books may hold it
                {dealIdRef.current && <> as <span className="font-mono">{dealIdRef.current}</span></>} with the answer lost, so a retry
                keeps that id and these figures and cannot book it twice. Check Trades if in doubt.
              </>}
              action={<>
                <Link to="/trades" className="btn-link text-meta">Open Trades →</Link>
                <button type="button" className="btn-link text-meta" onClick={startAnother} disabled={busy}>Start another instead</button>
              </>}
            >
              Trade may be booked — a retry can&rsquo;t book it twice.
            </StateLine>
          </div>
        )
      )}

      {/* The success line (§6): the ids, the day, the box in BoxLines' words
          — live once the party is re-read, projected until then. */}
      {submit.isSuccess && posted && (
        <div className="space-y-1.5">
          <StateLine
            tone="good"
            action={<>
              <Link to="/trades" className="btn-link text-meta">Trades →</Link>
              <Link to={partyHref} className="btn-link text-meta">Party page →</Link>
            </>}
          >
            ✓ Booked — margin <span className="whitespace-nowrap">{fmtAmount(posted.margin, posted.fiat, 2)} {displayAsset(posted.fiat)}</span>{posted.asOf ? ` as of ${fmtDate(posted.asOf)}` : ''}
          </StateLine>
          {/* The two boxes NOW — the projection until the party is re-read, the live box after. While the refresh is failed neither
              is claimed: the projection is a box nobody has read since the posting. */}
          {!unknown && (
            <p className="text-meta text-tg-hint">
              {posted.name} now: <span className="whitespace-nowrap">{boxWithAsset(fiatAfter, posted.fiat)}</span> · <span className="whitespace-nowrap">{boxWithAsset(coinAfter, posted.coin)}</span>{!live && ' (reading…)'}
            </p>
          )}
          <p className="flex flex-wrap gap-x-3 text-meta">
            <CopyRef value={posted.dealId} />
            {posted.money && <CopyRef value={posted.money.reference} />}
            {posted.delivery && <CopyRef value={posted.delivery.reference} />}
          </p>
          {!posted.money && <StateLine tone="warn">{posted.side === 'buy' ? 'Money in' : 'Coins in'} not recorded — add it from his page.</StateLine>}
          {!posted.delivery && <StateLine tone="warn">{posted.side === 'buy' ? 'Coins out' : 'Money out'} not recorded — add it from his page.</StateLine>}
          {posted.tradeDeduped && <StateLine tone="warn">Trade was already booked — its first date stands.</StateLine>}
        </div>
      )}

      {twin && (
        <RepeatPanel
          twin={twin}
          pending={busy || !!whenWords || unknown}
          // A twin TRADE that is already booked means this whole entry set was
          // done earlier — leave, like every other screen. A twin MOVEMENT that
          // is already booked stands in for that entry: mark it and carry on
          // with whatever is still to post.
          onAlreadyBooked={() => {
            const step = askingRef.current;
            clearTwin();
            if (step === 'trade') { nav(-1); return; }
            landedRef.current[step] = { reference: twin.reference ?? 'an earlier entry', deduped: true, prior: true };
            setLanded({ ...landedRef.current });
            send();
          }}
          onPostAnyway={() => { if (unknown) return; confirmedRef.current.add(`${askingRef.current}:${twin.kind}`); clearTwin(); send(); }}
        />
      )}

      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={!canPost}
          onClick={() => send(true)}
        >
          {busy ? 'Booking…'
            // The figures and the accounts are in order; only the party's read stands between the operator and the button.
            : ready && unknown ? 'Party not read'
            : landed.trade
              ? (remaining === 0 ? 'Finish' : `Retry the remaining ${remaining === 1 ? 'entry' : 'entries'}`)
              : `Book · ${entryWords}${whenIso && !whenWords ? ` · as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>
    </div>
  );
}

/** One movement: the tick, what it records, and the account it went through.
 *  `h-full` so the two boxes share one height in their grid row. */
function MovementBox({ checked, onChange, done, disabled, label, detail, children }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  /** The reference once this movement has posted — the box then reads as done. */
  done: string | null;
  disabled: boolean;
  label: string;
  detail: string;
  children: ReactNode;
}) {
  return (
    <div className={`h-full rounded-lg border px-3 py-2.5 space-y-2 ${checked ? 'border-stroke bg-panel' : 'border-stroke-soft'}`}>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="min-w-0">
          <span className="block text-body">{label}{done && <span className="text-pos"> · posted</span>}</span>
          <span className="block text-meta text-tg-hint mt-0.5">{done ? <span className="font-mono">{done}</span> : detail}</span>
        </span>
      </label>
      {checked && !done && <div>{children}</div>}
    </div>
  );
}
